import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import FileSaver from 'file-saver';
import { NotifyService } from 'helpers';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { downloadTemplateImportCSVFile } from 'services/subscription';

interface ImportCSV {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
}

const PopupImportCSV = ({ open, onClose, onUpload }: ImportCSV) => {
  const [file, setFile] = useState<File | null>(null);
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles?.length) return;

    const uploaded = acceptedFiles[0];

    const isCSV =
      uploaded.type === 'text/csv' ||
      uploaded.name.toLowerCase().endsWith('.csv');

    if (!isCSV) {
      NotifyService.error('Only .csv files are accepted');
      return;
    }

    setFile(uploaded);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
    onDrop,
  });

  const handleUpload = () => {
    if (!file) return;
    onUpload(file);
    setFile(null);
    onClose();
  };

  const handleRemoveFile = () => {
    setFile(null);
  };
  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadTemplateImportCSVFile();

      FileSaver.saveAs(res.data, 'Import_Subscription_Template.csv');
    } catch (e) {
      NotifyService.error(e);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import CSV</DialogTitle>

      <DialogContent>
        <Typography
          component="a"
          onClick={handleDownloadTemplate}
          download
          sx={{
            display: 'inline-block',
            mb: 2,
            color: 'primary.main',
            fontWeight: 600,
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          Download import file template
        </Typography>
        {!file && (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #D0D5DD',
              borderRadius: 2,
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? '#f9fafb' : 'transparent',
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: '#667085' }} />
            <Typography fontWeight={600}>Drag & drop CSV here</Typography>
            <Typography variant="body2" color="text.secondary">
              or click to choose file
            </Typography>
          </Box>
        )}
        {file && (
          <Box
            sx={{
              border: '1px solid #E4E7EC',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <DescriptionIcon color="action" />
              <Typography>{file.name}</Typography>
            </Box>

            <IconButton onClick={handleRemoveFile}>
              <DownloadIcon />
            </IconButton>
          </Box>
        )}

        <Typography mt={2} fontSize={13} color="text.secondary">
          Only .csv files are accepted
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" disabled={!file} onClick={handleUpload}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopupImportCSV;
